import React from 'react';
import PropTypes from 'prop-types';

import DraftMessage from '../../correspondances/DraftMessage';

const SmsResponseView = (props) => {
  const {communicationTarget, isRegenerating, onDiscard, onRegenerate, requester, response} = props;

  const [isSendingSms, setIsSendingSms] = React.useState(false);
  const [smsData, setSmsData] = React.useState(() => {
    return {
      message: response.message,
      from: [requester?.firstName, requester?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim(),
      delayUntil: 0,
    };
  });

  const updateSmsData = (key) => (value) => {
    setSmsData((prevSmsData) => ({
      ...prevSmsData,
      [key]: value,
    }));
  };

  React.useEffect(() => {
    setSmsData({
      message: response.message,
      from: [requester?.firstName, requester?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim(),
      delayUntil: 0,
    });
  }, [requester, response]);

  const canApproveAndSend = Boolean(communicationTarget?.sms) && Boolean(communicationTarget?.sendSms);

  const handleApproveAndSend = React.useCallback(async () => {
    if (!canApproveAndSend) {
      return;
    }

    setIsSendingSms(true);

    try {
      await communicationTarget.sendSms(smsData, requester, () => {
        onDiscard?.();
      });
    } finally {
      setIsSendingSms(false);
    }
  }, [canApproveAndSend, communicationTarget, onDiscard, requester, smsData]);

  const fields = [
    {
      key: 'to',
      type: 'readonly',
      expression: 'skills.SkillsModule.Sms.Fields.To',
      value: communicationTarget?.sms,
      sx: {
        mb: '8px',
      },
    },
    {
      key: 'from',
      type: 'editable',
      expression: 'skills.SkillsModule.Sms.Fields.From',
      value: smsData.from,
      onChange: updateSmsData('from'),
    },
  ];

  const actions = [
    {
      key: 'approve',
      variant: 'primary',
      expression: 'skills.SkillsModule.Sms.Actions.ApproveAndSend',
      disabled: isSendingSms || !canApproveAndSend,
      onClick: handleApproveAndSend,
    },
    {
      key: 'regenerate',
      variant: 'secondary',
      expression: 'skills.SkillsModule.Sms.Actions.Regenerate',
      disabled: isRegenerating,
      onClick: onRegenerate,
    },
    {
      key: 'discard',
      variant: 'tertiary',
      expression: 'skills.SkillsModule.Sms.Actions.Discard',
      onClick: onDiscard,
    },
  ];

  return (
    <DraftMessage
      titleExpression='skills.SkillsModule.Sms.Draft'
      fields={fields}
      bodyField={{
        value: smsData.message,
        onChange: updateSmsData('message'),
      }}
      actions={actions}
    />
  );
};

SmsResponseView.propTypes = {
  communicationTarget: PropTypes.shape({
    sendSms: PropTypes.func,
    sms: PropTypes.string,
  }),
  isRegenerating: PropTypes.bool,
  onDiscard: PropTypes.func,
  onRegenerate: PropTypes.func,
  requester: PropTypes.object.isRequired,
  response: PropTypes.shape({
    message: PropTypes.string,
  }),
};

export default React.memo(SmsResponseView);
